import React, { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { FieldError, FieldErrors } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { ReactComponent as DragAndDropIcon } from '@/assets/icons/drag-and-drop.svg';
import { ReactComponent as XCircleIcon } from '@/assets/icons/x-circle.svg';
import { ReactComponent as PlusIcon } from '@/assets/icons/plus.svg';
import { FileText } from 'lucide-react';
import { ReactComponent as MobileIcon } from '@/assets/icons/mobile.svg';
import { ReactComponent as CameraIcon } from '@/assets/icons/camera-1.svg';
import toast from 'react-hot-toast';

const MAX_TOTAL_UPLOAD_BYTES = 5 * 1024 * 1024; // 5MB total
const MAX_TOTAL_UPLOAD_LABEL = '5MB';
const FILE_SIZE_ERROR_MESSAGE = `Total upload size must be ${MAX_TOTAL_UPLOAD_LABEL} or smaller.`;

type FormFieldError = FieldError | undefined | FieldErrors<any>;

interface UploadFilesFieldProps {
  label: string;
  supportedFormats: string[];
  max: number;
  required?: boolean;
  error?: FormFieldError;
  value?: File[];
  onChange?: (files: File[]) => void;
  className?: string;
}

const getTotalFileSize = (fileList: File[]) => fileList.reduce((total, file) => total + file.size, 0);

const UploadFilesField: React.FC<UploadFilesFieldProps> = ({
  label,
  supportedFormats,
  max,
  required = false,
  error,
  value = [],
  onChange,
  className
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(value);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Helper function to check if file is an image
  const isImageFile = (file: File) => {
    return file.type.startsWith('image/');
  };

  // Handle file drop - append new files instead of replacing
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const remainingSlots = Math.max(0, max - uploadedFiles.length);
      const limitedAccepted = acceptedFiles.slice(0, remainingSlots);
      const limitedFiles = [...uploadedFiles, ...limitedAccepted];

      if (getTotalFileSize(limitedFiles) > MAX_TOTAL_UPLOAD_BYTES) {
        setUploadError(FILE_SIZE_ERROR_MESSAGE);
        toast.error(`Total upload size cannot exceed ${MAX_TOTAL_UPLOAD_LABEL}. Please upload smaller files.`);
        return;
      }

      setUploadError(null);
      setUploadedFiles(limitedFiles);
      if (onChange) {
        onChange(limitedFiles);
      }
    },
    [uploadedFiles, max, onChange]
  );

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    const typeRejection = fileRejections.find((rejection) =>
      rejection.errors.some((error) => error.code === 'file-invalid-type')
    );

    if (typeRejection) {
      setUploadError(null);
      toast.error(`File "${typeRejection.file.name}" is not a supported file type.`);
    }
  }, []);

  // Remove specific file
  const removeFile = useCallback(
    (indexToRemove: number) => {
      const updatedFiles = uploadedFiles.filter((_, index) => index !== indexToRemove);
      setUploadError(null);
      setUploadedFiles(updatedFiles);
      if (onChange) {
        onChange(updatedFiles);
      }
    },
    [uploadedFiles, onChange]
  );

  // Handle camera capture - use native camera app
  const handleCameraCapture = useCallback(() => {
    // Create a hidden file input that opens native camera
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera
    input.style.display = 'none';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const limitedFiles = [...uploadedFiles, file].slice(0, max);

        if (getTotalFileSize(limitedFiles) > MAX_TOTAL_UPLOAD_BYTES) {
          setUploadError(FILE_SIZE_ERROR_MESSAGE);
          toast.error(
            `Total upload size cannot exceed ${MAX_TOTAL_UPLOAD_LABEL}. Please capture or upload smaller files.`
          );
          return;
        }

        setUploadError(null);
        setUploadedFiles(limitedFiles);
        if (onChange) {
          onChange(limitedFiles);
        }
        toast.success('Photo captured successfully!');
      }
    };

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }, [uploadedFiles, max, onChange]);

  // Create accept object from supported formats
  const acceptObject = supportedFormats.reduce(
    (acc, format) => {
      switch (format.toLowerCase()) {
        case 'jpeg':
        case 'jpg':
          acc['image/jpeg'] = ['.jpeg', '.jpg'];
          break;
        case 'png':
          acc['image/png'] = ['.png'];
          break;
        case 'pdf':
          acc['application/pdf'] = ['.pdf'];
          break;
        default:
          break;
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptObject,
    maxFiles: max,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: uploadedFiles.length >= max
  });

  const formErrorMessage = error && typeof error === 'object' && 'message' in error ? error.message : '';
  const displayErrorMessage = uploadError || formErrorMessage;

  return (
    <div className={className}>
      {label && (
        <label className="text-sm font-medium leading-[100%] inline-block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Dropzone Area */}
      <div
        {...getRootProps()}
        className={`
          flex flex-col items-start md:items-center md:justify-center gap-4 md:border md:border-dashed rounded-[16px] md:py-6 md:px-4 cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300'}
          ${isDragAccept ? 'border-green-500 bg-green-50' : ''}
          ${isDragReject || uploadError ? 'border-red-500 bg-red-50' : ''}
          ${uploadedFiles.length > 0 ? 'border-none bg-none flex-row !items-start !p-0 !cursor-default' : ''}
          ${error || uploadError ? '!border-red-500' : ''}
        `}
      >
        <input {...getInputProps()} />

        {uploadedFiles.length > 0 ? (
          <>
            {/* WEB */}
            <div className="hidden md:flex flex-wrap gap-4">
              {uploadedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="border space-y-2 p-3 rounded-[16px] w-[144px]">
                  <div className="flex items-start justify-between">
                    {isImageFile(file) ? (
                      <img
                        className="w-[68px] h-[68px] rounded-[8px] object-cover"
                        src={URL.createObjectURL(file)}
                        alt="uploaded-file"
                      />
                    ) : (
                      <div className="w-[68px] h-[68px] rounded-[8px] bg-gray-100 flex items-center justify-center">
                        <FileText color="#DBD6FF" className="w-14 h-14 text-gray-800" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="hover:scale-110 transition-transform"
                    >
                      <XCircleIcon />
                    </button>
                  </div>
                  <p title={file.name} className="text-sm font-semibold truncate">
                    {file.name}
                  </p>
                  <span className="text-xs text-primary font-medium">
                    {file.size >= 1024 * 1024
                      ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                      : `${(file.size / 1024).toFixed(2)} KB`}
                  </span>
                </div>
              ))}

              {/* Upload More Image button - only show if less than max files */}
              {uploadedFiles.length < max && (
                <div
                  className="cursor-pointer border border-dashed flex items-center justify-center p-3 rounded-[16px] w-[144px] transition-all duration-300 hover:bg-primary/5 hover:border-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <PlusIcon width={14} height={14} />
                    <p className="font-semibold text-primary text-center text-sm">Upload More Files</p>
                  </div>
                </div>
              )}
            </div>

            {/* MOBILE */}
            <div className="md:hidden w-full grid grid-cols-2 gap-4">
              {uploadedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="col-span-1 border space-y-2 p-3 rounded-[16px]">
                  <div className="flex items-start justify-between">
                    {isImageFile(file) ? (
                      <img
                        className="w-[68px] h-[68px] rounded-[8px] object-cover"
                        src={URL.createObjectURL(file)}
                        alt="uploaded-file"
                      />
                    ) : (
                      <div className="w-[68px] h-[68px] rounded-[8px] bg-gray-100 flex items-center justify-center">
                        <FileText color="#DBD6FF" className="w-14 h-14 text-gray-800" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="hover:scale-110 transition-transform"
                    >
                      <XCircleIcon />
                    </button>
                  </div>
                  <p title={file.name} className="text-sm font-semibold truncate">
                    {file.name}
                  </p>
                  <span className="text-xs text-primary font-medium">
                    {file.size >= 1024 * 1024
                      ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                      : `${(file.size / 1024).toFixed(2)} KB`}
                  </span>
                </div>
              ))}

              {/* Upload More Image button - only show if less than max files */}
              {uploadedFiles.length < max && (
                <div className="col-span-2 flex items-stretch justify-between gap-2">
                  <button
                    type="button"
                    className="flex-1 flex items-center justify-start gap-2 bg-white rounded-[16px] p-4 space-y-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCameraCapture();
                    }}
                  >
                    <CameraIcon width={16} height={16} />
                    <p className="font-semibold text-left text-xs leading-[100%]">CAPTURE</p>
                  </button>
                  <button
                    type="button"
                    className="flex-1 flex items-center justify-start gap-2 bg-white rounded-[16px] p-4 space-y-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                  >
                    <MobileIcon width={16} height={16} />
                    <p className="font-semibold text-left text-xs leading-[100%]">LOCAL STORAGE</p>
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="hidden md:flex flex-col items-center justify-center gap-1">
            <DragAndDropIcon />
            <p className="font-semibold">
              {isDragActive
                ? isDragReject
                  ? 'File type not supported'
                  : 'Drop the files here'
                : 'Drag and Drop Files here'}
            </p>
            <span className="text-xs">Paste files, or</span>
          </div>
        )}

        {uploadedFiles.length === 0 && (
          <>
            <Button
              type="button"
              variant={'primary-reverse'}
              className="hidden md:block rounded-full font-semibold px-6"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              Upload from Computer
            </Button>
            <div className="md:hidden w-full flex items-stretch justify-between gap-2">
              <button
                type="button"
                className="flex-1 flex flex-col bg-white rounded-[16px] p-4 space-y-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCameraCapture();
                }}
              >
                <CameraIcon width={44} height={44} />
                <p className="font-semibold text-left text-base leading-[100%]">CAPTURE A IMAGE</p>
              </button>
              <button
                type="button"
                className="flex-1 flex flex-col bg-white rounded-[16px] p-4 space-y-1"
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
              >
                <MobileIcon width={44} height={44} />
                <p className="font-semibold text-left text-base leading-[100%]">UPLOAD FROM LOCAL STORAGE</p>
              </button>
            </div>
          </>
        )}
      </div>

      {/* File type info */}
      <p className="text-xs text-secondary text-center mt-2">
        Supported formats: {supportedFormats.join(', ')} (Max {max} files, {MAX_TOTAL_UPLOAD_LABEL} total) •{' '}
        {uploadedFiles.length}/{max} files uploaded
      </p>

      {/* Error message */}
      {displayErrorMessage && <p className="mt-2 text-xs text-red-600">{displayErrorMessage as string}</p>}
    </div>
  );
};

export default UploadFilesField;
